import React, { useEffect, useState } from "react";

import { useGetCurrentCompanyApplicationQuery, useSyncAllPartnerDocumentsMutation } from "EmployerApplication/api";
import { ApplicationLayout } from "EmployerApplication/Components";
import { DocumentCardsSection } from "EmployerApplication/Components/DocumentCardsSection";
import { documentUpload, wageDocumentUpload, validateEmployerApplicationDocument } from "EmployerApplication/Components/documentPicker/utils";
import { showWageDocumentPicker } from "EmployerApplication/Selectors";
import { documentsUploadPageURL, reviewEmployeeEmailsURL, paymentURL } from "EmployerApplication/urls";
import { updateEntities } from "EntitiesApi/Actions";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { BasicLoading } from "ZeroRuby";

import AgentHeader from "Common/Agent/AgentHeader";
import BodyText from "Common/BodyText";
import Form from "Common/Form";
import NavigationControls from "Common/NavigationControls";
import SecurityBox from "Common/SecurityBox/SecurityBox";
import { useThemeContext } from "Theme/Context";
import { Surface, SurfaceHeader, SurfaceSection } from "Theme/Surface";

export const DocumentsUploadForm = ({ companyApplication, showCobalt, history, backURL, nextURL }) => {
  const companyPolicyId = companyApplication?.id;
  const onDocumentUpload = (form) => documentUpload(form, companyPolicyId); // TODO: In ER App this should become an RTK mutation.
  const onWageDocumentUpload = (form) => wageDocumentUpload(form, companyPolicyId); // TODO: In ER App this should become an RTK mutation.
  const onvalidateEmployerApplicationDocument = (document) => validateEmployerApplicationDocument(document, companyPolicyId);

  const dispatch = useDispatch();
  const onDocumentUploadSuccess = (responseJson) => dispatch(updateEntities(responseJson));

  const handleSubmit = (e) => {
    e.preventDefault();
    AnalyticsTracking.trackEvent("document_upload_save_clicked", {});
    history.push(nextURL || paymentURL);
  };

  const handleClickBack = () => {
    AnalyticsTracking.trackEvent("back_button_clicked", {
      location: "document_page",
    });
  };

  let contents;
  const { uploadDocsProps } = companyApplication;
  /** Document requirements filtered down to essentials only */
  const requiredSubmissions = uploadDocsProps.filter((doc) => !doc.meta?.supplementaryRequirement);

  // NOTE: Highly unlikely, probably impossible, to hit this scenario
  if (requiredSubmissions.length == 0) {
    contents = (
      <div>
        <BodyText size="large" my={3} agent>
          Based on your answers, no further documentation will be required to process your application.
        </BodyText>
        <br />
      </div>
    );
  } else {
    contents = (
      <DocumentCardsSection
        uploadDocsProps={requiredSubmissions}
        showWageDocumentPicker={useSelector(showWageDocumentPicker)}
        onDocumentUpload={onDocumentUpload}
        onDocumentUploadSuccess={onDocumentUploadSuccess}
        onWageDocumentUpload={onWageDocumentUpload}
        validateEmployerApplicationDocument={onvalidateEmployerApplicationDocument}
      />
    );
  }

  return (
    <Form action={documentsUploadPageURL} method="post" encType="multipart/form-data" onSubmit={handleSubmit}>
      {/* TODO - remove SurfaceHeader in favor of section title prop when Cobalt launches */}
      {!showCobalt && <SurfaceHeader>Required Documents</SurfaceHeader>}
      <SurfaceSection title="Required Documents">
        {contents}
        <SecurityBox />
      </SurfaceSection>
      <NavigationControls backLink={backURL || reviewEmployeeEmailsURL} onClickBack={handleClickBack} />
    </Form>
  );
};

// This is a wrapper component that syncs all partner documents before rendering the DocumentsUploadPage.
// This is necessary because the DocumentsUploadPage relies on the partner documents being synced before
// is is mounted so that it refetches the company application with the updated documents.
const DocumentSyncWrapper = ({ children, ...rest }) => {
  const [documentsSynced, setDocumentsSynced] = useState(false);
  const [syncAllPartnerDocuments] = useSyncAllPartnerDocumentsMutation();
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;

  const syncDocuments = async () => {
    await syncAllPartnerDocuments({
      companyPolicyId,
    });

    setDocumentsSynced(true);
  };

  useEffect(() => {
    if (!companyPolicyId) return;
    syncDocuments();
  }, [companyPolicyId]);

  if (documentsSynced) {
    return children(rest);
  }
  return <BasicLoading />;
};

const DocumentsUploadPage = ({ history, backURL, nextURL }) => {
  const { intuit, cobalt } = useThemeContext();
  const showCobalt = cobalt && !intuit;

  const { data: companyApplication, isLoading } = useGetCurrentCompanyApplicationQuery({
    refetchOnMountOrArgChange: true,
  });

  const isRenewal = companyApplication?.isRenewalPolicy;

  if (isLoading) {
    return <BasicLoading />;
  }

  return (
    <Surface>
      <SurfaceHeader>Finalize Application</SurfaceHeader>
      <SurfaceSection>
        <AgentHeader>
          Now, let's gather some documents that the insurance company requires. If you don't have them handy, you can probably get them from your accountant or
          payroll provider.
        </AgentHeader>
      </SurfaceSection>
      <DocumentsUploadForm companyApplication={companyApplication} showCobalt={showCobalt} history={history} backURL={backURL} nextURL={nextURL} />
    </Surface>
  );
};

const DocumentsUploadPageWithSync = (props) => <DocumentSyncWrapper {...props}>{(rest) => <DocumentsUploadPage {...rest} />}</DocumentSyncWrapper>;

export default withRouter(DocumentsUploadPageWithSync);
